import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import * as RadioPrimitive from "@radix-ui/react-radio-group";
import { useId, type ComponentProps, type ReactNode } from "react";
import { cn } from "../lib/cn";
import { Icon } from "./icon";

/**
 * Target size (WCAG 2.2 SC 2.5.8, 24 x 24 CSS px): the control element itself is 24px (44px with `touch`) so the measured box and the
 * clickable area agree; the visible 16px box is its `::before`, and negative margins hand the extra pixels back to the layout (nothing shifts).
 * The focus ring is the global 2px outline pulled 2px inward (`-outline-offset-2`): it encloses the 16px box exactly as the old
 * 2px-offset ring did.
 */
const HIT_TARGET =
  "relative inline-flex shrink-0 cursor-pointer items-center justify-center before:absolute before:content-[''] before:border before:border-line-strong before:bg-surface before:transition-colors focus-visible:-outline-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

export interface CheckboxProps extends Omit<ComponentProps<typeof CheckboxPrimitive.Root>, "children"> {
  /** Visible label rendered next to the box (clickable). Omit and pass aria-label for table cells. */
  label?: ReactNode;
  description?: ReactNode;
  /** A thumb-sized row (phone screens): the row, its label and the button around the box are at least 44px tall; the 16px box does not change. */
  touch?: boolean;
}

/** Radix checkbox (supports `checked="indeterminate"`): a 16px box inside a 24px button (44px with `touch`), see HIT_TARGET. */
export function Checkbox({ label, description, className, id, touch = false, ...rest }: CheckboxProps) {
  const generated = useId();
  const controlId = id ?? `cb-${generated}`;
  const descriptionId = description ? `${controlId}-desc` : undefined;
  const box = (
    <CheckboxPrimitive.Root
      id={controlId}
      aria-describedby={descriptionId}
      className={cn(
        "peer text-on-primary before:rounded-xs data-[state=checked]:before:border-primary data-[state=checked]:before:bg-primary data-[state=indeterminate]:before:border-primary data-[state=indeterminate]:before:bg-primary",
        HIT_TARGET,
        touch ? "-m-3.5 size-11 rounded-[16px] before:inset-3.5 focus-visible:-outline-offset-12" : "-m-1 size-6 rounded-sm before:inset-1",
        className,
      )}
      {...rest}
    >
      <CheckboxPrimitive.Indicator className="group relative flex items-center justify-center">
        <Icon name="check" className="size-3 stroke-3 group-data-[state=indeterminate]:hidden" />
        <Icon name="minus" className="hidden size-3 stroke-3 group-data-[state=indeterminate]:block" />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
  if (!label) return box;
  return (
    <div className={cn("flex gap-2", touch ? "min-h-11 items-center" : "items-start")}>
      <span className={cn("flex", !touch && "mt-0.5")}>{box}</span>
      <div className="min-w-0">
        <label htmlFor={controlId} className={cn("cursor-pointer text-sm text-fg peer-disabled:cursor-not-allowed", touch && "flex min-h-11 items-center")}>
          {label}
        </label>
        {description ? (
          <p id={descriptionId} className="text-xs text-fg-subtle">
            {description}
          </p>
        ) : null}
      </div>
    </div>
  );
}

export interface RadioOption<T extends string> {
  value: T;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
}

export interface RadioGroupProps<T extends string>
  extends Omit<ComponentProps<typeof RadioPrimitive.Root>, "value" | "onValueChange" | "defaultValue" | "children"> {
  options: readonly RadioOption<T>[];
  value: T;
  onValueChange: (value: T) => void;
  /** `cards` renders bordered option cards (audience / plan pickers). */
  variant?: "list" | "cards";
}

/** Arrow keys move one option forward or back, wrapping like Radix roving focus (left-to-right layouts). */
const ARROW_STEP: Readonly<Record<string, 1 | -1>> = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 };

/** Radix radio group: roving focus with arrow keys, one tab stop. */
export function RadioGroup<T extends string>({ options, value, onValueChange, variant = "list", className, ...rest }: RadioGroupProps<T>) {
  const baseId = useId();
  const handle = (next: string) => {
    const match = options.find((o) => o.value === next);
    if (match) onValueChange(match.value);
  };
  // Radix selects the item that roving focus lands on only while the arrow key is still held, and it moves focus a tick
  // after keydown: a short press (a fast typist, a switch device, an automated keyboard) moved focus without selecting.
  // A native radio group selects on the arrow itself, so the wrapper does the same.
  // Capture phase: the Radix item prevents the default of an arrow key before the event bubbles to the root.
  const { onKeyDownCapture, ...rootProps } = rest;
  const selectByArrow: ComponentProps<typeof RadioPrimitive.Root>["onKeyDownCapture"] = (event) => {
    onKeyDownCapture?.(event);
    const step = ARROW_STEP[event.key];
    if (step === undefined || event.altKey || event.ctrlKey || event.metaKey) return;
    const enabled = options.filter((o) => !o.disabled);
    const from = (event.target as HTMLElement).getAttribute("value") ?? value;
    const at = enabled.findIndex((o) => o.value === from);
    if (at < 0 || enabled.length < 2) return;
    const next = enabled[(at + step + enabled.length) % enabled.length];
    if (next && next.value !== value) onValueChange(next.value);
  };
  return (
    <RadioPrimitive.Root value={value} onValueChange={handle} onKeyDownCapture={selectByArrow} className={cn("grid gap-2", className)} {...rootProps}>
      {options.map((option) => {
        const itemId = `${baseId}-${option.value}`;
        return (
          <div
            key={option.value}
            className={cn(
              "flex items-start gap-2.5",
              variant === "cards" && "rounded-lg border border-line p-3 has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary-soft",
            )}
          >
            <RadioPrimitive.Item
              id={itemId}
              value={option.value}
              disabled={option.disabled}
              className={cn(HIT_TARGET, "-mx-1 -mb-1 -mt-0.5 size-6 rounded-pill before:inset-1 before:rounded-pill data-[state=checked]:before:border-primary")}
            >
              <RadioPrimitive.Indicator className="relative size-2 rounded-pill bg-primary" />
            </RadioPrimitive.Item>
            <label htmlFor={itemId} className="min-w-0 cursor-pointer">
              <span className="block text-sm font-semibold text-fg">{option.label}</span>
              {option.description ? <span className="block text-xs text-fg-subtle">{option.description}</span> : null}
            </label>
          </div>
        );
      })}
    </RadioPrimitive.Root>
  );
}
